import React, { useMemo, useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { FiArrowLeft, FiPlus } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { PRIMARY } from '../../theme';
import api from '../../utils/api';
import { useReferenceData } from '../../contexts/ReferenceDataContext';

const normalizeFeedbackParameters = (response) => {
  const rows = Array.isArray(response?.data)
    ? response.data
    : Array.isArray(response)
    ? response
    : [];

  return rows.map((item, index) => ({
    id: item?._id || item?.id || index + 1,
    name:
      item?.name ||
      item?.parameter ||
      item?.title ||
      item?.feedbackParameter ||
      '-',
    isActive: item?.isActive !== false,
  }));
};

const normalizeScoreKey = (value) =>
  String(value || '')
    .toLowerCase()
    .replace(/[^a-z0-9]/g, '');

const getNestedPayload = (response) =>
  response?.data?.data || response?.data || response || {};

const hasScoreKeyMatch = (source, normalizedTarget) => {
  if (!source || typeof source !== 'object' || Array.isArray(source)) return false;
  return Object.keys(source).some(
    (key) => normalizeScoreKey(key) === normalizedTarget
  );
};

/** Matches Teams Events page primary actions (e.g. Add Team). */
const TEAMS_TOOLBAR_PRIMARY_BTN = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  flexShrink: 0,
  height: 34,
  padding: '0 14px',
  border: 'none',
  color: '#ffffff',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 600,
  whiteSpace: 'nowrap',
  boxShadow: '0 1px 2px rgba(78, 115, 223, 0.28)',
};

const FeedbackParametersPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { feedbackParametersResponse, loading, refreshFeedbackParameters } = useReferenceData();

  const handleBack = () => {
    const fromPath = String(location.state?.from || '').trim();
    navigate(fromPath || '/admin/settings');
  };
  const feedbackParameters = useMemo(
    () => normalizeFeedbackParameters(feedbackParametersResponse || {}),
    [feedbackParametersResponse]
  );
  const [newParameter, setNewParameter] = useState('');
  const [creating, setCreating] = useState(false);
  const [editingId, setEditingId] = useState('');
  const [editingValue, setEditingValue] = useState('');
  const [savingId, setSavingId] = useState('');
  const [deletingId, setDeletingId] = useState('');
  const [togglingId, setTogglingId] = useState('');

  const handleCreate = async (event) => {
    event.preventDefault();
    const value = newParameter.trim();
    if (!value) {
      toast.error('Please enter feedback parameter');
      return;
    }

    try {
      setCreating(true);
      await api.createFeedbackParameter(value);
      setNewParameter('');
      toast.success('Feedback parameter created successfully');
      await refreshFeedbackParameters();
    } catch (error) {
      console.error('Error creating feedback parameter:', error);
      toast.error(error.message || 'Failed to create feedback parameter');
    } finally {
      setCreating(false);
    }
  };

  const startEdit = (parameter) => {
    setEditingId(parameter.id);
    setEditingValue(parameter.name || '');
  };

  const cancelEdit = () => {
    setEditingId('');
    setEditingValue('');
  };

  const handleSaveEdit = async (parameterId) => {
    const value = editingValue.trim();
    if (!value) {
      toast.error('Please enter feedback parameter');
      return;
    }

    try {
      setSavingId(parameterId);
      await api.updateFeedbackParameter(parameterId, value);
      toast.success('Feedback parameter updated successfully');
      cancelEdit();
      await refreshFeedbackParameters();
    } catch (error) {
      console.error('Error updating feedback parameter:', error);
      toast.error(error.message || 'Failed to update feedback parameter');
    } finally {
      setSavingId('');
    }
  };

  const handleToggleActive = async (parameter) => {
    const next = !parameter.isActive;
    try {
      setTogglingId(parameter.id);
      await api.updateFeedbackParameter(parameter.id, { isActive: next });
      toast.success(
        next
          ? 'Parameter is active for newly created matches'
          : 'Parameter is inactive for newly created matches (existing matches unchanged)'
      );
      await refreshFeedbackParameters();
    } catch (error) {
      console.error('Error updating parameter status:', error);
      toast.error(error.message || 'Failed to update parameter status');
    } finally {
      setTogglingId('');
    }
  };

  const handleDelete = async (parameter) => {
    try {
      setDeletingId(parameter.id);
      const normalizedTarget = normalizeScoreKey(parameter.name);
      const feedbackResponse = await api.getEmployeeFeedbackSummary();
      const payload = getNestedPayload(feedbackResponse);
      const detailRows = Array.isArray(payload?.details)
        ? payload.details
        : Array.isArray(payload?.rows)
        ? payload.rows
        : Array.isArray(payload?.events)
        ? payload.events
        : [];
      const summaryRows = Array.isArray(payload?.summary)
        ? payload.summary
        : Array.isArray(payload?.summaries)
        ? payload.summaries
        : [];

      const isUsedInDetails = detailRows.some((row) => {
        if (!row || typeof row !== 'object') return false;
        const scoresSource =
          row?.scores && typeof row.scores === 'object' ? row.scores : null;
        return (
          hasScoreKeyMatch(scoresSource, normalizedTarget) ||
          hasScoreKeyMatch(row, normalizedTarget)
        );
      });

      const isUsedInSummary = summaryRows.some((row) => {
        if (!row || typeof row !== 'object') return false;
        const scoresSource =
          row?.scores && typeof row.scores === 'object'
            ? row.scores
            : row?.averages && typeof row.averages === 'object'
            ? row.averages
            : null;
        return (
          hasScoreKeyMatch(scoresSource, normalizedTarget) ||
          hasScoreKeyMatch(row, normalizedTarget)
        );
      });

      if (isUsedInDetails || isUsedInSummary) {
        toast.error(
          `Cannot delete "${parameter.name}" because it is already used in feedback data`
        );
        return;
      }

      const confirmed = window.confirm(`Delete parameter "${parameter.name}"?`);
      if (!confirmed) return;

      await api.deleteFeedbackParameter(parameter.id);
      await refreshFeedbackParameters();
      toast.success('Feedback parameter deleted successfully');
    } catch (error) {
      console.error('Error deleting feedback parameter:', error);
      toast.error(error.message || 'Failed to delete feedback parameter');
    } finally {
      setDeletingId('');
    }
  };

  return (
    <div style={{ padding: '16px' }}>
      <div
        style={{
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
          padding: '16px',
        }}
      >
        <div
          style={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
            gap: '16px',
            marginBottom: '14px',
            flexWrap: 'wrap',
          }}
        >
          <div style={{ flex: '0 1 auto', minWidth: 0, maxWidth: '100%' }}>
            <form
              onSubmit={handleCreate}
              style={{
                display: 'flex',
                flexDirection: 'row',
                flexWrap: 'nowrap',
                alignItems: 'center',
                gap: 8,
                maxWidth: '100%',
              }}
            >
              <input
                type="text"
                placeholder="Enter feedback parameter"
                value={newParameter}
                onChange={(e) => setNewParameter(e.target.value)}
                style={{
                  flex: '1 1 auto',
                  minWidth: 400,
                  maxWidth: '100%',
                  boxSizing: 'border-box',
                  height: 34,
                  padding: '0 10px',
                  border: '1px solid #dee2e6',
                  borderRadius: 6,
                  fontSize: 12,
                  color: '#344054',
                  background: '#fff',
                }}
              />
              <button
                type="submit"
                disabled={creating}
                style={{
                  ...TEAMS_TOOLBAR_PRIMARY_BTN,
                  flexShrink: 0,
                  background: PRIMARY,
                  cursor: creating ? 'not-allowed' : 'pointer',
                  opacity: creating ? 0.7 : 1,
                }}
              >
                <FiPlus size={15} strokeWidth={2.5} aria-hidden style={{ color: '#fff' }} />
                {creating ? 'Adding...' : 'Add'}
              </button>
            </form>
          </div>
          <button
            type="button"
            onClick={handleBack}
            style={{
              flex: '0 0 auto',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px',
              height: '36px',
              padding: '0 12px',
              border: '1px solid #e5e7eb',
              background: '#ffffff',
              color: '#1f2937',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '12px',
              fontWeight: 600,
              boxSizing: 'border-box',
            }}
          >
            <FiArrowLeft size={14} strokeWidth={2.2} aria-hidden />
            Back
          </button>
        </div>

        <div style={{ border: '1px solid #e5e7eb', borderRadius: '10px', overflowX: 'hidden' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead style={{ background: '#f9fafb' }}>
              <tr>
                <th style={{ textAlign: 'left', padding: '10px', fontSize: '12px', color: '#6b7280' }}>
                  Parameter
                </th>
                <th style={{ textAlign: 'center', padding: '10px', fontSize: '12px', color: '#6b7280' }}>
                  Status
                </th>
                <th style={{ textAlign: 'center', padding: '10px', fontSize: '12px', color: '#6b7280' }}>
                  Actions
                </th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td
                    colSpan="3"
                    style={{ padding: '14px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}
                  >
                    Loading feedback parameters...
                  </td>
                </tr>
              ) : feedbackParameters.length === 0 ? (
                <tr>
                  <td
                    colSpan="3"
                    style={{ padding: '14px', textAlign: 'center', color: '#9ca3af', fontSize: '13px' }}
                  >
                    No feedback parameters found.
                  </td>
                </tr>
              ) : (
                feedbackParameters.map((parameter) => (
                  <tr key={parameter.id} style={{ borderTop: '1px solid #e5e7eb' }}>
                    <td
                      style={{
                        padding: '10px',
                        fontSize: '13px',
                        color: parameter.isActive ? '#111827' : '#9ca3af',
                        fontWeight: 600,
                      }}
                    >
                      {editingId === parameter.id ? (
                        <input
                          type="text"
                          value={editingValue}
                          onChange={(e) => setEditingValue(e.target.value)}
                          style={{
                            width: '100%',
                            maxWidth: '360px',
                            padding: '8px 10px',
                            border: '1px solid #d1d5db',
                            borderRadius: '6px',
                            fontSize: '13px',
                            fontWeight: 500,
                          }}
                        />
                      ) : (
                        parameter.name
                      )}
                    </td>
                    <td
                      style={{
                        padding: '10px',
                        fontSize: '12px',
                        color: '#4b5563',
                        textAlign: 'center',
                      }}
                    >
                      {editingId === parameter.id ? (
                        <span style={{ color: '#9ca3af' }}>—</span>
                      ) : (
                        <div
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            gap: '8px',
                            flexWrap: 'wrap',
                          }}
                        >
                          <span
                            style={{
                              
                              display: 'inline-block',
                              padding: '2px 8px',
                              borderRadius: '999px',
                              fontSize: '11px',
                              fontWeight: 600,
                              background: parameter.isActive ? '#dcfce7' : '#f3f4f6',
                              color: parameter.isActive ? '#166534' : '#6b7280',
                            }}
                          >
                            {parameter.isActive ? 'Active' : 'Inactive'}
                          </span>
                          <button
                            type="button"
                            onClick={() => handleToggleActive(parameter)}
                            disabled={togglingId === parameter.id}
                            style={{
                              border: '1px solid #d1d5db',
                              background: 'white',
                              color: '#374151',
                              borderRadius: '6px',
                              padding: '4px 10px',
                              cursor: togglingId === parameter.id ? 'not-allowed' : 'pointer',
                              fontSize: '12px',
                              opacity: togglingId === parameter.id ? 0.6 : 1,
                            }}
                          >
                            {togglingId === parameter.id
                              ? '...'
                              : parameter.isActive
                              ? 'Deactivate'
                              : 'Activate'}
                          </button>
                        </div>
                      )}
                    </td>
                    <td style={{ padding: '10px', textAlign: 'center' }}>
                      {editingId === parameter.id ? (
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <button
                            type="button"
                            onClick={() => handleSaveEdit(parameter.id)}
                            disabled={savingId === parameter.id}
                            style={{
                              border:
                                savingId === parameter.id
                                  ? '1px solid transparent'
                                  : '1px solid #bfdbfe',
                              background: '#eff6ff',
                              color: '#1d4ed8',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              cursor: savingId === parameter.id ? 'not-allowed' : 'pointer',
                              opacity: savingId === parameter.id ? 0.7 : 1,
                              fontSize: '12px',
                            }}
                          >
                            {savingId === parameter.id ? 'Saving...' : 'Save'}
                          </button>
                          <button
                            type="button"
                            onClick={cancelEdit}
                            style={{
                              border: '1px solid #d1d5db',
                              background: 'white',
                              color: '#374151',
                              borderRadius: '6px',
                              padding: '6px 10px',
                              cursor: 'pointer',
                              fontSize: '12px',
                            }}
                          >
                            Cancel
                          </button>
                        </div>
                      ) : (
                        <div style={{ display: 'inline-flex', gap: '8px' }}>
                          <span className="icon-tooltip-wrap">
                            <button
                              type="button"
                              aria-label="Edit"
                              onClick={() => startEdit(parameter)}
                              style={{
                                border: '1px solid #a7f3d0',
                                background: '#ecfeff',
                                color: '#0f766e',
                                borderRadius: '6px',
                                width: '28px',
                                height: '28px',
                                cursor: 'pointer',
                                fontSize: '14px',
                                lineHeight: 1,
                              }}
                            >
                              ✏
                            </button>
                            <span className="icon-tooltip-bubble">Edit</span>
                          </span>
                          <span className="icon-tooltip-wrap">
                            <button
                              type="button"
                              aria-label="Delete"
                              onClick={() => handleDelete(parameter)}
                              disabled={deletingId === parameter.id}
                              style={{
                                border:
                                  deletingId === parameter.id
                                    ? '1px solid transparent'
                                    : '1px solid #fecdd3',
                                background: '#fff1f2',
                                color: '#e11d48',
                                borderRadius: '6px',
                                width: '28px',
                                height: '28px',
                                cursor: deletingId === parameter.id ? 'not-allowed' : 'pointer',
                                opacity: deletingId === parameter.id ? 0.6 : 1,
                                fontSize: '14px',
                                lineHeight: 1,
                              }}
                            >
                              🗑
                            </button>
                            <span className="icon-tooltip-bubble">Delete</span>
                          </span>
                        </div>
                      )}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default FeedbackParametersPage;
